import i18n from '../i18n'
import { ApiResponse, TUser, Tag, ThemeSettings, Try, toFailure, toSuccess } from '../types/commonTypes'
import { camelToSnakeCase } from '../util/string'
import { createAxios } from './axios'
import { GetLibraryGamesResponse } from './gameTypes'
import { getTheme } from './typeConverters'
import {
  ApiTheme,
  GetUserInfoParams,
  LoginParams,
  NewTrialParams,
  PasswordResetParams,
  PlayerAccountJoinGameResponse,
  PlayerAccountSearchResponse,
  PlayerLoginParams,
  PlayerLoginResponse,
  PostFeedbackParams,
  PostFeedbackResponse,
  UpdateUserInfoParams,
  User,
} from './userTypes'

export const TEST_USER_AUTHORIZATION = null //'ad5dec1598a7043657d92c2c990b7ce8' //'testuser@seppo.io'
export const TEST_USER_ID = null

export async function getUserInfo({ authorization = TEST_USER_AUTHORIZATION }: GetUserInfoParams): Promise<Try<User>> {
  try {
    const response = await createAxios(authorization).get<User>('/user_info', {
      params: {},
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

const mapTUserKeyToUserDataKey = (key: keyof TUser) => {
  return key === 'chatName' ? 'real_name' : camelToSnakeCase(key)
}

export async function updateUserInfo({
  authorization = TEST_USER_AUTHORIZATION,
  userId,
  updateData,
}: UpdateUserInfoParams): Promise<Try<User>> {
  try {
    const response = await createAxios(authorization).put<User>(`/users/${userId}.json`, {
      user: { [mapTUserKeyToUserDataKey(updateData.key)]: updateData.value },
      id: userId,
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function fetchTheme(themeName: string): Promise<Try<ThemeSettings>> {
  try {
    const response = await createAxios().get<ApiTheme>('/theme', {
      params: {
        name: themeName,
      },
    })

    const { data } = response

    return toSuccess(getTheme(data))
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function login({
  userName,
  password,
  language = 'en',
  teacherCode,
  myRandom,
  gToken,
  fbToken,
  resendPassword = false,
  trialConfirm,
}: LoginParams): Promise<Try<User>> {
  try {
    const response = await createAxios().post<User>('/users/login', {
      /* eslint-disable camelcase */
      ux3: true,
      login_type: 'json_reply',
      disable_cookies: true,
      username: userName,
      password: password,
      language: language,
      random: myRandom,
      ...((teacherCode || '').length > 0 ? { teacher_code: teacherCode } : {}),
      ...((gToken || '').length > 0 ? { g_token: gToken } : {}),
      ...((fbToken || '').length > 0 ? { fb_token: fbToken, email: userName } : {}),
      ...(resendPassword ? { resend_password: resendPassword } : {}),
      ...(trialConfirm ? { trial_confirm_email: trialConfirm } : {}),
      /* eslint-enable camelcase */
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

// A new user object has been created already, with this request the backend creates a Trial account and returns suggested games
export async function startNewTrial(params: NewTrialParams): Promise<Try<GetLibraryGamesResponse>> {
  try {
    const query = Object.entries<string>(params)
      .map(([key, value]) => `${key}=${encodeURI(value)}`)
      .join('&')
    const response = await createAxios().get<GetLibraryGamesResponse>(`/ux3_trial?${query}`, {})

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

// TODO: this implementation is not that perfect, should be done properly
export const sendPasswordResetLink = async ({
  authorization = TEST_USER_AUTHORIZATION,
  userEmail: userName,
}: PasswordResetParams): Promise<Try<any>> => {
  try {
    await createAxios(authorization).post('/password_resets', {
      name: userName,
    })
    return toSuccess(true)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function submitTrialFeedback({
  authorization = TEST_USER_AUTHORIZATION,
  feedbackMessage,
}: PostFeedbackParams): Promise<Try<PostFeedbackResponse>> {
  try {
    const response = await createAxios(authorization).post<PostFeedbackResponse>('/trial_feedback', {
      feedback_text: feedbackMessage,
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

//https://staging.seppo.io/gamer_accounts/sort_players_by_classes.json
export async function searchPlayerAccounts({
  authorization = TEST_USER_AUTHORIZATION,
  tagIds,
  search,
  page,
  showAll = false,
}: {
  authorization?: null
  tagIds: string[]
  search: string
  page: number
  showAll?: boolean
}): Promise<Try<PlayerAccountSearchResponse>> {
  try {
    const response = await createAxios(authorization).post<PlayerAccountSearchResponse>(
      '/gamer_accounts/sort_players_by_classes.json',
      {
        class_ids: { '0': tagIds.length ? tagIds : '0' }, // a bit strange format required by BE, "class_ids"=>{"0"=>["0"]} means to BE that no tag selected
        only_selected_classes: true,
        player_name_input: search,
        page_number: page,
        ...(showAll ? { show_all: showAll } : {}),
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function orderPasswordResetLink({
  email,
  language,
}: {
  email: string
  language: string
}): Promise<Try<ApiResponse>> {
  try {
    const response = await createAxios(null).post<ApiResponse>('/order_password_reset_link.json', {
      name: email,
      l: language,
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function resetPassword({
  token,
  password,
  passwordConfirmation,
  language,
}: {
  token: string
  password: string
  passwordConfirmation: string
  language: string
}): Promise<Try<ApiResponse>> {
  try {
    const response = await createAxios(null).post<ApiResponse>('/set_new_password.json', {
      reset_token: token,
      password,
      password_confirmation: passwordConfirmation,
      l: language,
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function playerLoginRequest({
  pinCode,
  teamName,
  language,
  teamMemberNames,
  email,
  playerEmail,
  emailSessionId,
}: PlayerLoginParams): Promise<Try<PlayerLoginResponse>> {
  try {
    const response = await createAxios().post<PlayerLoginResponse>('/users/login', {
      /* eslint-disable camelcase */
      login_type: 'json_reply',
      disable_cookies: true,
      simple_pincode: pinCode,
      team_name: teamName,
      language_field: language.toLowerCase(),
      ...(teamMemberNames && { team_member_names: teamMemberNames }),
      ...(email && { email }),
      ...((playerEmail || '').length > 0 ? { player_email: playerEmail } : {}),
      ...(emailSessionId && { email_session_id: emailSessionId }),
      /* eslint-enable camelcase */
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function playerAccountLoginRequest({
  email,
  password,
  language,
}: {
  email: string
  password: string
  language: string
}): Promise<Try<ApiResponse>> {
  try {
    const response = await createAxios(null).post<ApiResponse>('/gamer_accounts/gamer_account_login.json', {
      login_type: 'json_reply',
      gamer_username: email,
      gamer_password: password,
      language,
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function playerAccountJoinNewGameRequest({
  pinCode,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  pinCode: string
  authorization?: string | null
}): Promise<Try<PlayerAccountJoinGameResponse>> {
  try {
    const response = await createAxios(authorization).post<PlayerAccountJoinGameResponse>(
      '/gamer_accounts/start_new_game.json',
      {
        login_type: 'json_reply',
        pin_code: pinCode,
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function playerAccountJoinNewGameWithNameRequest({
  teamName,
  newGameId,
  newUserId,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  teamName: string
  newGameId: string
  newUserId: string
  authorization?: string | null
}): Promise<Try<PlayerAccountJoinGameResponse>> {
  try {
    const response = await createAxios(authorization).post<PlayerAccountJoinGameResponse>(
      '/gamer_accounts/start_game_with_name.json',
      {
        login_type: 'json_reply',
        team_name: teamName,
        new_game_id: newGameId,
        new_user_id: newUserId,
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function playerAccountUpdateRequest({
  playerAccount,
  language,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  playerAccount?: { email: string; playerName: string; password: string; gamerAccountConfirmPassword: string }
  language?: string
  authorization?: string | null
}): Promise<Try<PlayerAccountJoinGameResponse>> {
  try {
    const player_account = playerAccount
      ? {
          email: playerAccount.email,
          player_name: playerAccount.playerName,
          password: playerAccount.password,
          gamer_account_confirm_password: playerAccount.gamerAccountConfirmPassword,
        }
      : undefined
    const response = await createAxios(authorization).post<PlayerAccountJoinGameResponse>(
      '/gamer_accounts/update.json',
      {
        login_type: 'json_reply',
        ux3: true,
        player_account: player_account,
        language: language,
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function getPlayerAccountPasswordTokenRequest({
  email,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  email: string
  authorization?: string | null
}): Promise<Try<PlayerAccountJoinGameResponse>> {
  try {
    const response = await createAxios(authorization).post<PlayerAccountJoinGameResponse>(
      '/gamer_accounts/lost_password.json',
      {
        login_type: 'json_reply',
        ux3: true,
        email: email,
        language: i18n.language,
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function changePlayerAccountPasswordRequest({
  token,
  newPassword,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  token: string
  newPassword: string
  authorization?: string | null
}): Promise<Try<ApiResponse>> {
  try {
    const response = await createAxios(authorization).post<PlayerAccountJoinGameResponse>(
      '/gamer_accounts/change_password.json',
      {
        login_type: 'json_reply',
        ux3: true,
        token: token,
        new_pwd: newPassword,
        language: i18n.language,
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function managePlayerAccountTags({
  checkedTags,
  checkedPlayers,
  removedTags,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  checkedTags?: Tag[]
  checkedPlayers?: number[]
  removedTags?: Tag[]
  authorization?: string | null
}): Promise<Try<ApiResponse>> {
  const checkedTagIds = checkedTags?.map((tag) => {
    return tag.id
  })

  let checkedPlayersString: string = ''
  checkedPlayers?.forEach((player, index) => {
    index === 0
      ? (checkedPlayersString = 'checked_players[]' + player)
      : (checkedPlayersString = checkedPlayersString + '&checked_players[]' + player)
  })

  const removedTagIds = removedTags?.map((tag) => {
    return tag.id
  })

  try {
    const response = await createAxios(authorization).post<PlayerAccountJoinGameResponse>(
      '/gamer_accounts/manage_player_classes.json',
      {
        login_type: 'json_reply',
        added_class_ids: checkedTagIds,
        checked_players: checkedPlayers,
        removed_class_ids: removedTagIds,
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function deleteBusinessTag({
  tag,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  tag: Tag
  authorization?: string | null
}): Promise<Try<{}>> {
  try {
    const response = await createAxios(authorization).delete<{}>(`/admin/instructor_classes/${tag.id}`)

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function editBusinessTag({
  tagId,
  newText,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  tagId: number
  newText: string
  authorization?: string | null
}): Promise<Try<ApiResponse>> {
  try {
    const response = await createAxios(authorization).put<PlayerAccountJoinGameResponse>(
      `/admin/instructor_classes/${tagId}`,
      {
        login_type: 'json_reply',
        instructor_class: { group_tag: newText },
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function createBusinessTag({
  newTag,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  newTag: string
  authorization?: string | null
}): Promise<Try<ApiResponse>> {
  try {
    const response = await createAxios(authorization).post<PlayerAccountJoinGameResponse>(
      '/admin/instructor_classes/',
      {
        login_type: 'json_reply',
        instructor_class: { group_tag: newTag },
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function removeBusinessPlayerAccount({
  playerId,
  authorization = TEST_USER_AUTHORIZATION,
}: {
  playerId: number
  authorization?: string | null
}): Promise<Try<ApiResponse>> {
  try {
    const response = await createAxios(authorization).post<ApiResponse>(
      '/gamer_accounts/remove_gamer_account_from_instructor.json',
      {
        login_type: 'json_reply',
        id: playerId,
      },
    )

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}
